import fundOpportunityContentCache from 'FundOpportunityContentCache';
import { BreadcrumbsManager } from '../../context/BreadcrumbsManager';
import { FundOpportunityEOIController } from './controller';
import { FundOpportunityProps, FundOpportunityState } from './types';

export function componentDidMount(ctx: FundOpportunityEOIController): void {
  if (ctx.state.investor) {
    const fundOpportunity = ctx.state.investor.fundOpportunity;

    if (fundOpportunity) {
      if (BreadcrumbsManager.peek()?.href?.match(/(opportunities|fundOpportunities)\/[^/]+/)) {
        BreadcrumbsManager.pop();
      }

      BreadcrumbsManager.push({
        label: fundOpportunity.summaryTitle || fundOpportunity.name || 'Opportunity',
        href: `/opportunities/${fundOpportunity.id}`,
      });
    }
  }
  const lookupEOI = () => {
    ctx.setState({
      indicativeInvestmentInput: ctx.state.investor?.indicativeInvestment?.toString(),
      indicativeInvestment: ctx.state.investor?.indicativeInvestment ?? undefined,
      eoiIndicativeInvestment: ctx.state.investor?.indicativeInvestment ?? undefined,
      originalEOIIndicativeInvestment: ctx.state.investor?.indicativeInvestment ?? undefined,
      eoiSubmittedOn: ctx.state.investor?.eoiSubmittedOn ?? undefined,
      loadedEOI: true,
    });
  };

  if (!ctx.state.loadedEOI) {
    if (ctx.currentStatus() === 'EOI') {
      lookupEOI();
    } else {
      ctx.setState({ loadedEOI: true });
    }
  }

  ctx.loadStaticPdfAvailability();
  // Load custodian status (only in non-admin mode)
  if (!ctx.props.adminView && !ctx.props.advisorMode) {
    ctx.setState({ loadingCustodianStatus: true });
    ctx
      .getBackendService()
      .getCustodianStatus()
      .then((custodianStatus) => {
        ctx.setState({
          isCustodian: custodianStatus.isCustodian,
          loadingCustodianStatus: false,
        });
      })
      .catch((error) => {
        console.error('Error loading custodian status:', error);
        // Default to false if error occurs
        ctx.setState({
          isCustodian: false,
          loadingCustodianStatus: false,
        });
      });
  } else {
    // In the admin preview, not a custodian
    ctx.setState({ isCustodian: false, loadingCustodianStatus: false });
  }

  if (ctx.state.hasStorageToken.tokenSet && !ctx.props.prefetchedContent) {
    ctx.loadContent();
  }
}

export function loadContent(ctx: FundOpportunityEOIController) {
  // If prefetched content is provided, skip loading from cache
  if (ctx.props.prefetchedContent) {
    return Promise.resolve();
  }

  let actualFundOpportunityId = ctx.state.fundOpportunity!.id;

  if (!ctx.props.adminView) {
    actualFundOpportunityId =
      ctx.state.fundOpportunity!.linkedContentExternalId || ctx.state.fundOpportunity!.id;
  }

  fundOpportunityContentCache.clearCache(actualFundOpportunityId, 'FundOpportunity');

  return new Promise<void>((resolve) => {
    fundOpportunityContentCache
      .getContent(
        actualFundOpportunityId,
        'FundOpportunity',
        !!ctx.props.adminView,
        !ctx.props.adminView,
      )
      .then((content) => {
        if (content) {
          ctx.dataRead(JSON.stringify(content));
        } else {
          ctx.setState({ newTemplate: true });
        }

        resolve();
      });
  });
}

export function waitForCarouselImages(ctx: FundOpportunityEOIController) {
  const assetImageLoaded = document.querySelectorAll('.carousel-container .asset-image-dynamic');
  const assetImageLoading = document.querySelectorAll(
    '.carousel-container .asset-image-dynamic-loading',
  );

  const assetImageMissing = document.querySelectorAll('.carousel-container .asset-image-missing');

  const hasElements =
    assetImageLoaded.length > 0 || assetImageMissing.length > 0 || assetImageLoading.length > 0;

  const waitForLoad = () => {
    new Promise((resolve) => setTimeout(resolve, 200)).then(() => {
      ctx.setState({
        waitLoad: {
          tries: (ctx.state.waitLoad?.tries || 0) + 1,
          loaded: false,
        },
      });
    });
  };

  if (!hasElements) {
    waitForLoad();
    return;
  }

  if (assetImageLoading.length > 0) {
    waitForLoad();
    return;
  }

  ctx.setState({
    waitLoad: {
      tries: ctx.state.waitLoad?.tries || 0,
      loaded: true,
    },
  });
}

export function componentDidUpdate(
  ctx: FundOpportunityEOIController,
  prevProps: Readonly<FundOpportunityProps>,
  prevState: Readonly<FundOpportunityState>,
): void {
  if (ctx.state.hasStorageToken.tokenSet && !prevState.hasStorageToken.tokenSet) {
    ctx.loadContent();
    return;
  }

  // If assetStorageToken is provided as prop, don't call backend
  // The token is already available, just mark it as set
  if (
    !ctx.state.hasStorageToken.tokenSet &&
    !ctx.state.contentManagementInterface &&
    !ctx.props.prefetchedContent
  ) {
    if (ctx.props.assetStorageToken) {
      // Token provided as prop, use it without calling backend
      ctx.setState({
        hasStorageToken: {
          tries: 0,
          tokenSet: true,
        },
      });
    } else {
      // No token provided, try to get from backend
      ctx
        .getBackendService()
        .waitForAssetStorage()
        .then((result) => {
          ctx.setState({
            hasStorageToken: {
              tries: ctx.state.hasStorageToken.tries + 1,
              tokenSet: result,
            },
          });
        });
    }
  }

  if (ctx.state.printMode) {
    if (
      (!prevState.contentManagementInterface && ctx.state.contentManagementInterface) ||
      (!prevState.loadedEOI && ctx.state.loadedEOI)
    ) {
      if (ctx.state.contentManagementInterface && ctx.state.loadedEOI) {
        ctx.setState({ waitLoad: { tries: 0, loaded: false } });
      }
    }
  }

  if (JSON.stringify(prevState.waitLoad) !== JSON.stringify(ctx.state.waitLoad)) {
    if (!ctx.state.waitLoad?.loaded) {
      const hasImageCarousel =
        ctx.state.contentManagementInterface?.content?.find(
          (item) => item.type === 'ImageCarousel',
        ) || undefined;

      if (hasImageCarousel) {
        ctx.waitForCarouselImages();
      }
    } else {
      ctx.generateHTML();
    }
  }

  if (ctx.state.pdfJobStatus !== prevState.pdfJobStatus) {
    if (ctx.state.pdfJobStatus?.status !== 'COMPLETE') {
      new Promise<void>((resolve) => setTimeout(resolve, 500)).then(() => {
        ctx.checkPDFJob();
      });
    } else {
      ctx.setState({ loadingPDF: false });
      const pdfUrl = ctx.state.pdfJobStatus.url;
      if (pdfUrl) {
        const element = document.createElement('a');
        element.href = pdfUrl;
        element.target = '_blank';
        element.download = `EOI Opportunity ${ctx.state.fundOpportunity!.name}.pdf`;
        document.body.appendChild(element);
        element.click();
        element.remove();
      }

      if (ctx.state.printCallback) {
        ctx.state.printCallback();
        ctx.setState({ printCallback: undefined });
      }
    }
  }

  if (ctx.props.investorId) {
    const latestInvestor = ctx.props.investorContext?.investors?.find((investor) => {
      return ctx.props.investorId === investor.id;
    });

    // Check if investors array reference changed (context update) or if updatedAt changed
    const investorsChanged =
      prevProps.investorContext?.investors !== ctx.props.investorContext?.investors;
    const investorDataChanged =
      latestInvestor && latestInvestor.updatedAt !== ctx.state.investor?.updatedAt;

    // Also check if suggestion fields changed (even if updatedAt is the same)
    const suggestionChanged =
      latestInvestor &&
      (latestInvestor.suggestedIndicativeInvestment !==
        ctx.state.investor?.suggestedIndicativeInvestment ||
        latestInvestor.indicativeInvestmentSuggestedBy?.id !==
          ctx.state.investor?.indicativeInvestmentSuggestedBy?.id);

    if (investorsChanged && (investorDataChanged || suggestionChanged)) {
      // Reset all EOI-related state when investor data refreshes
      // This ensures the entire EOI section refreshes with the new data
      ctx.setState(() => ({
        ...ctx.state,
        investor: latestInvestor,
        editEOI: false,
        updateEoi: false,
        withdrawEOI: false,
        withdrawingEOI: false,
        submittingEOI: false,
        eoiConfirmed: false,
        indicativeInvestmentInput: latestInvestor?.indicativeInvestment?.toString(),
        indicativeInvestment: latestInvestor?.indicativeInvestment ?? undefined,
        eoiIndicativeInvestment: latestInvestor?.indicativeInvestment ?? undefined,
        originalEOIIndicativeInvestment: latestInvestor?.indicativeInvestment ?? undefined,
        eoiSubmittedOn: latestInvestor?.eoiSubmittedOn ?? undefined,
        loadedEOI: true,
      }));
    }
  }

  if (prevState.fundOpportunity?.id !== ctx.state.fundOpportunity?.id) {
    ctx.loadStaticPdfAvailability();
  }
}
